import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule, Clock, AlertTriangle } from 'lucide-angular';
import { STUDENT_ASSIGNMENT_THEME_COLOR } from '../../services/student-assignment.models';

@Component({
  selector: 'app-quiz-timer',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './quiz-timer.html',
  styleUrl: './quiz-timer.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuizTimerComponent implements OnChanges, OnDestroy {
  @Input() expiresAt: Date | null = null;

  @Output() readonly expired = new EventEmitter<void>();

  protected readonly themeColor = STUDENT_ASSIGNMENT_THEME_COLOR;

  protected readonly icons = {
    Clock,
    AlertTriangle,
  };

  // Remaining time in seconds
  protected readonly remainingSeconds = signal<number>(0);

  // Warning threshold (5 minutes)
  protected readonly warningThreshold = 5 * 60;

  // Critical threshold (1 minute)
  protected readonly criticalThreshold = 60;

  private intervalId: ReturnType<typeof setInterval> | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['expiresAt']) {
      this.startTimer();
    }
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  private startTimer(): void {
    this.stopTimer();

    if (!this.expiresAt) return;

    // Calculate initial remaining time
    this.updateRemainingTime();

    // Update every second
    this.intervalId = setInterval(() => {
      this.updateRemainingTime();
    }, 1000);
  }

  private stopTimer(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private updateRemainingTime(): void {
    if (!this.expiresAt) return;

    const now = Date.now();
    const expiresAtMs = this.expiresAt.getTime();
    const remaining = Math.max(0, Math.floor((expiresAtMs - now) / 1000));

    this.remainingSeconds.set(remaining);

    if (remaining <= 0) {
      this.stopTimer();
      this.expired.emit();
    }
  }

  protected get isWarning(): boolean {
    const remaining = this.remainingSeconds();
    return remaining > 0 && remaining <= this.warningThreshold;
  }

  protected get isCritical(): boolean {
    const remaining = this.remainingSeconds();
    return remaining > 0 && remaining <= this.criticalThreshold;
  }

  protected get formattedTime(): string {
    const total = this.remainingSeconds();
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}
